<script setup lang="ts">
import type { LevelOption, SubjectCategory, Theme } from '#shared/utils/constants'

definePageMeta({
  name: 'articles',
})

const { data: articles, suspense } = useArticleCards()

await suspense()

const levels = useRouteQuery<LevelOption['value'][]>('level', [], {
  mode: 'replace',
  transform: (value) => Array.isArray(value) ? value : [value],
})

const subjects = useRouteQuery<SubjectCategory[]>('subject', [], {
  mode: 'replace',
  transform: (value) => Array.isArray(value) ? value : [value],
})

const themes = useRouteQuery<Theme[]>('theme', [], {
  mode: 'replace',
  transform: (value) => Array.isArray(value) ? value : [value],
})

const PAGE_SIZE = 12

const count = useRouteQuery<number>('count', PAGE_SIZE, {
  mode: 'replace',
  transform: (value) => {
    const n = Number(value)
    return Number.isNaN(n) || n < 1 ? PAGE_SIZE : n
  },
})

const filtered = computed(() => (articles.value ?? []).filter((card) => (
  (!levels.value.length || levels.value.some((l) => card.levels?.includes(l))) &&
  (!subjects.value.length || subjects.value.some((s) => card.subject?.includes(s))) &&
  (!themes.value.length || themes.value.some((t) => card.theme?.includes(t)))
)))

const sliced = computed(() => filtered.value.slice(0, count.value))

const hasMore = computed(() => sliced.value.length < filtered.value.length)
</script>

<template>
  <main id="main-content" class="pt-16">
    <UContainer class="text-center">
      <h1 id="articles-card-title" class="mb-4 text-base text-green-40 uppercase">
        Artikler
      </h1>
      <h2 class="mb-16 font-headline text-5xl text-green-50">
        Inspirasjon til undervisning
      </h2>
      <div class="mb-20 flex flex-wrap justify-center gap-4">
        <USelect
          v-model="levels"
          placeholder="Velg trinn"
          variant="soft"
          multiple
          size="xl"
          :items="LEVEL_OPTIONS.filter(o => articles?.some(card => card.levels?.includes(o.value)))"
          label-key="title"
          :ui="{
            base: 'text-green-50 bg-white rounded-full min-w-72 px-legacy-m py-legacy-xs',
            trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform size-5 text-current',
          }"
        />
        <USelect
          v-model="subjects"
          placeholder="Velg fag"
          variant="soft"
          multiple
          size="xl"
          :items="SUBJECT_OPTIONS.filter(o => articles?.some(card => card.subject?.includes(o.value)))"
          label-key="title"
          :ui="{
            base: 'text-green-50 bg-white rounded-full min-w-72 px-legacy-m py-legacy-xs',
            trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform size-5 text-current',
          }"
        />
        <USelect
          v-model="themes"
          placeholder="Velg tema"
          variant="soft"
          multiple
          size="xl"
          :items="THEME_OPTIONS.filter(o => articles?.some(card => card.theme?.includes(o.value)))"
          label-key="title"
          :ui="{
            base: 'text-green-50 bg-white rounded-full min-w-72 px-legacy-m py-legacy-xs',
            trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform size-5 text-current',
          }"
        />
      </div>
      <section
        id="articles-cards"
        aria-labelledby="articles-card-title"
        class="mb-16 flex flex-wrap justify-center gap-10"
      >
        <CardsArticle
          v-for="article in sliced"
          :key="article.id"
          :article="article"
        />
        <NoResults v-if="sliced.length === 0">
          Prøv å endre filtrering for å finne inspirerende artikler
        </NoResults>
      </section>
      <UButton
        v-if="hasMore"
        class="mb-32 rounded-full bg-yellow-legacy-30 px-legacy-m py-3 text-lg text-green-50 hover:bg-yellow-legacy-40"
        size="lg"
        @click="count += PAGE_SIZE"
      >
        Vis mer
      </UButton>
    </UContainer>
    <CtaNewsletter />
  </main>
</template>
